import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["query", "searchResults", "followingList", "noFollowingMessage", "followersList", "noFollowersMessage", "pendingList", "pendingSection", "followingTab", "followersTab", "friendsTab", "followingPane", "followersPane", "friendsPane", "followingContent", "followersContent", "friendsContent"]
  static values = { followingUrl: String, followersUrl: String, friendsUrl: String }

  #followingLoaded = false
  #followersLoaded = false
  #friendsLoaded = false

  connect() {
    this.debounceTimer = null
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    const savedTab = localStorage.getItem('follows_tab')
    if (savedTab === 'followers') {
      this.followingPaneTarget.classList.add("d-none")
      this.followersPaneTarget.classList.remove("d-none")
      this.followingTabTarget.classList.remove("active")
      this.followersTabTarget.classList.add("active")
      this.#loadFollowers()
    } else if (savedTab === 'friends' && this.hasFriendsTabTarget) {
      this.followingPaneTarget.classList.add("d-none")
      this.friendsPaneTarget.classList.remove("d-none")
      this.followingTabTarget.classList.remove("active")
      this.friendsTabTarget.classList.add("active")
      this.#loadFriends()
    } else {
      this.#loadFollowing()
    }
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    document.removeEventListener("click", this.boundClickOutside)
  }

  debounceSearch() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.performSearch(), 300)
  }

  onFocus() {
    const query = this.queryTarget.value.trim()
    if (query.length >= 2) this.performSearch()
  }

  performSearch() {
    const query = this.queryTarget.value.trim()
    if (query.length < 2) {
      this.searchResultsTarget.classList.add("d-none")
      this.searchResultsTarget.innerHTML = ""
      return
    }

    fetch("/social/search?q=" + encodeURIComponent(query))
      .then(response => response.json())
      .then(data => {
        this.searchResultsTarget.innerHTML = ""

        if (data.users.length === 0) {
          this.searchResultsTarget.innerHTML = '<div class="follows-search-empty">No users found</div>'
        } else {
          data.users.forEach(user => {
            this.searchResultsTarget.appendChild(this.buildSearchItem(user))
          })
        }

        this.searchResultsTarget.classList.remove("d-none")
      })
  }

  buildSearchItem(user) {
    const item = document.createElement("div")
    item.className = "follows-search-item"
    item.dataset.userId = user.id

    let imageHtml
    if (user.image) {
      imageHtml = `<img src="${user.image}" alt="${user.username}" class="flow-user-image">`
    } else {
      imageHtml = `<div class="flow-user-placeholder">${user.username.charAt(0).toUpperCase()}</div>`
    }

    const btnClass = user.is_following ? "btn-danger" : "btn-primary"
    const btnIcon = user.is_following ? "bi-person-dash" : "bi-person-plus"
    const btnText = user.is_following ? "Unfollow" : "Follow"

    item.innerHTML =
      `<div class="d-flex align-items-center gap-2">` +
        imageHtml +
        `<a href="/@${user.profile_username}">${user.username}</a>` +
      `</div>` +
      `<button type="button" class="btn btn-sm ${btnClass}" ` +
        `data-action="click->follows#toggleFollow" ` +
        `data-user-id="${user.id}" ` +
        `data-following="${user.is_following}" ` +
        `data-user-image="${user.image || ''}"><i class="bi ${btnIcon} me-1"></i>${btnText}</button>`

    return item
  }

  toggleFollow(event) {
    const button = event.currentTarget
    const userId = button.dataset.userId
    const isFollowing = button.dataset.following === "true"
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    button.disabled = true
    button.textContent = isFollowing ? "Unfollowing..." : "Following..."

    fetch(`/social/toggle/${userId}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          button.disabled = false
          button.innerHTML = `<i class="bi bi-person-dash me-1"></i>Unfollow`
          alert(data.error)
          return
        }

        if (data.pending) {
          button.dataset.following = "false"
          button.innerHTML = '<i class="bi bi-hourglass me-1"></i>Pending'
          button.classList.remove("btn-primary")
          button.classList.add("btn-secondary")
          button.disabled = true
        } else if (data.following) {
          button.dataset.following = "true"
          button.innerHTML = '<i class="bi bi-person-dash me-1"></i>Unfollow'
          button.classList.remove("btn-primary")
          button.classList.add("btn-danger")
          this.addToFollowingList(userId, data.username, button.dataset.userImage)
        } else {
          button.dataset.following = "false"
          button.innerHTML = '<i class="bi bi-person-plus me-1"></i>Follow'
          button.classList.remove("btn-danger")
          button.classList.add("btn-primary")
          this.removeFromFollowingList(userId)
        }
      })
      .catch(() => {
        const icon = isFollowing ? "bi-person-dash" : "bi-person-plus"
        const text = isFollowing ? "Unfollow" : "Follow"
        button.innerHTML = `<i class="bi ${icon} me-1"></i>${text}`
      })
      .finally(() => {
        button.disabled = false
      })
  }

  addToFollowingList(userId, username, userImage) {
    if (this.hasNoFollowingMessageTarget) {
      const cardBody = this.noFollowingMessageTarget.parentElement
      this.noFollowingMessageTarget.remove()
      const list = document.createElement("div")
      list.className = "list-group list-group-flush"
      list.dataset.followsTarget = "followingList"
      cardBody.appendChild(list)
    }

    const list = this.followingListTarget
    if (list.querySelector(`[data-user-id="${userId}"]`)) return

    let imageHtml
    if (userImage) {
      imageHtml = `<img src="${userImage}" alt="${username}" class="flow-user-image">`
    } else {
      imageHtml = `<div class="flow-user-placeholder">${username.charAt(0).toUpperCase()}</div>`
    }

    const item = document.createElement("div")
    item.className = "list-group-item d-flex align-items-center justify-content-between"
    item.dataset.userId = userId
    item.innerHTML =
      `<div class="d-flex align-items-center gap-2">` +
        imageHtml +
        `<span>${username}</span>` +
      `</div>` +
      `<button type="button" class="btn btn-sm btn-danger" ` +
        `data-action="click->follows#toggleFollow" ` +
        `data-user-id="${userId}" ` +
        `data-following="true"><i class="bi bi-person-dash me-1"></i>Unfollow</button>`

    list.insertBefore(item, list.firstChild)
  }

  removeFromFollowingList(userId) {
    if (!this.hasFollowingListTarget) return

    const item = this.followingListTarget.querySelector(`[data-user-id="${userId}"]`)
    if (item) item.remove()

    if (this.followingListTarget.children.length === 0) {
      const cardBody = this.followingListTarget.parentElement
      this.followingListTarget.remove()
      const msg = document.createElement("div")
      msg.className = "text-center py-4"
      msg.dataset.followsTarget = "noFollowingMessage"
      msg.innerHTML = '<i class="bi bi-person-plus display-6 text-muted"></i><h6 class="text-muted mt-3">You\'re not following anyone yet</h6><p class="text-muted small mb-0">Use the search above to find people to follow.</p>'
      cardBody.appendChild(msg)
    }
  }

  showFollowing() {
    this.followingPaneTarget.classList.remove("d-none")
    this.followersPaneTarget.classList.add("d-none")
    if (this.hasFriendsPaneTarget) this.friendsPaneTarget.classList.add("d-none")
    this.followingTabTarget.classList.add("active")
    this.followersTabTarget.classList.remove("active")
    if (this.hasFriendsTabTarget) this.friendsTabTarget.classList.remove("active")
    localStorage.setItem('follows_tab', 'following')
    this.#loadFollowing()
  }

  showFollowers() {
    this.followingPaneTarget.classList.add("d-none")
    this.followersPaneTarget.classList.remove("d-none")
    if (this.hasFriendsPaneTarget) this.friendsPaneTarget.classList.add("d-none")
    this.followingTabTarget.classList.remove("active")
    this.followersTabTarget.classList.add("active")
    if (this.hasFriendsTabTarget) this.friendsTabTarget.classList.remove("active")
    localStorage.setItem('follows_tab', 'followers')
    this.#loadFollowers()
  }

  showFriends() {
    this.followingPaneTarget.classList.add("d-none")
    this.followersPaneTarget.classList.add("d-none")
    this.friendsPaneTarget.classList.remove("d-none")
    this.followingTabTarget.classList.remove("active")
    this.followersTabTarget.classList.remove("active")
    this.friendsTabTarget.classList.add("active")
    localStorage.setItem('follows_tab', 'friends')
    this.#loadFriends()
  }

  showConfirmFriendshipModal(event) {
    const checkbox = event.currentTarget
    checkbox.checked = false
    this.friendshipUserId = checkbox.dataset.userId
    const modal = document.getElementById("confirmFriendshipModal")
    modal.querySelector("[data-friendship-username]").textContent = checkbox.dataset.username
    this.confirmFriendshipModalInstance = new bootstrap.Modal(modal)
    this.confirmFriendshipModalInstance.show()
  }

  confirmFriendship() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const btn = document.getElementById("confirmFriendshipBtn")
    btn.disabled = true
    btn.textContent = "Confirming..."

    fetch(`/friendships/${this.friendshipUserId}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(() => {
        this.confirmFriendshipModalInstance.hide()
        this.#reloadFriends()
      })
      .finally(() => {
        btn.disabled = false
        btn.innerHTML = '<i class="bi bi-heart me-1"></i>Confirm'
      })
  }

  showRemoveFriendModal(event) {
    const button = event.currentTarget
    this.removeFriendshipId = button.dataset.friendshipId
    this.removeFriendUserId = button.dataset.friendId
    const modal = document.getElementById("removeFriendModal")
    modal.querySelector("[data-remove-friend-username]").textContent = button.dataset.username
    this.removeFriendModalInstance = new bootstrap.Modal(modal)
    this.removeFriendModalInstance.show()
  }

  removeFriendship() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const btn = document.getElementById("removeFriendBtn")
    btn.disabled = true
    btn.textContent = "Removing..."

    fetch(`/friendships/${this.removeFriendshipId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(() => {
        this.removeFriendModalInstance.hide()
        this.#reloadFriends()
      })
      .finally(() => {
        btn.disabled = false
        btn.innerHTML = '<i class="bi bi-person-dash me-1"></i>Remove Friend'
      })
  }

  #loadFollowing() {
    if (this.#followingLoaded) return
    this.#followingLoaded = true

    fetch(this.followingUrlValue, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => { this.followingContentTarget.innerHTML = html })
      .catch(() => { this.followingContentTarget.innerHTML = '<p class="text-muted">Failed to load.</p>' })
  }

  #loadFollowers() {
    if (this.#followersLoaded) return
    this.#followersLoaded = true

    fetch(this.followersUrlValue, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => { this.followersContentTarget.innerHTML = html })
      .catch(() => { this.followersContentTarget.innerHTML = '<p class="text-muted">Failed to load.</p>' })
  }

  #loadFriends() {
    if (this.#friendsLoaded) return
    this.#friendsLoaded = true

    fetch(this.friendsUrlValue, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => { this.friendsContentTarget.innerHTML = html })
      .catch(() => { this.friendsContentTarget.innerHTML = '<p class="text-muted">Failed to load.</p>' })
  }

  #reloadFriends() {
    this.#friendsLoaded = false
    this.friendsContentTarget.innerHTML =
      `<div class="d-flex justify-content-center py-3">` +
        `<div class="spinner-border spinner-border-sm text-secondary" role="status">` +
          `<span class="visually-hidden">Loading...</span>` +
        `</div>` +
      `</div>`
    this.#loadFriends()
  }

  showBlockModal(event) {
    const button = event.currentTarget
    this.blockUserId = button.dataset.userId
    const modal = document.getElementById("blockModal")
    modal.querySelector("[data-block-username]").textContent = button.dataset.username
    this.blockModalInstance = new bootstrap.Modal(modal)
    this.blockModalInstance.show()
  }

  confirmBlock() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const confirmBtn = document.getElementById("blockConfirmBtn")
    confirmBtn.disabled = true
    confirmBtn.textContent = "Blocking..."

    fetch(`/social/block/${this.blockUserId}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(() => {
        this.blockModalInstance.hide()
        this.removeFromFollowersList(this.blockUserId)
        this.removeFromFollowingList(this.blockUserId)
      })
      .finally(() => {
        confirmBtn.disabled = false
        confirmBtn.innerHTML = '<i class="bi bi-slash-circle me-1"></i>Block'
      })
  }

  removeFromFollowersList(userId) {
    if (!this.hasFollowersListTarget) return

    const item = this.followersListTarget.querySelector(`[data-follower-id="${userId}"]`)
    if (item) item.remove()

    if (this.followersListTarget.children.length === 0) {
      const cardBody = this.followersListTarget.parentElement
      this.followersListTarget.remove()
      const msg = document.createElement("div")
      msg.className = "text-center py-4"
      msg.dataset.followsTarget = "noFollowersMessage"
      msg.innerHTML = '<i class="bi bi-people display-6 text-muted"></i><h6 class="text-muted mt-3">No followers yet</h6><p class="text-muted small mb-0">When other users follow you, they\'ll appear here.</p>'
      cardBody.appendChild(msg)
    }
  }

  showConfirmModal(event) {
    const button = event.currentTarget
    this.confirmUserId = button.dataset.userId
    this.confirmUsername = button.dataset.username
    const modal = document.getElementById("confirmFollowModal")
    modal.querySelector("[data-confirm-username]").textContent = button.dataset.username
    this.confirmModalInstance = new bootstrap.Modal(modal)
    this.confirmModalInstance.show()
  }

  allowFollow() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const allowBtn = document.getElementById("confirmAllowBtn")
    allowBtn.disabled = true
    allowBtn.textContent = "Allowing..."

    fetch(`/social/confirm/${this.confirmUserId}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(() => {
        this.confirmModalInstance.hide()
        this.removeFromPendingList(this.confirmUserId)
        this.addToFollowersList(this.confirmUserId, this.confirmUsername)
      })
      .finally(() => {
        allowBtn.disabled = false
        allowBtn.innerHTML = '<i class="bi bi-check-lg me-1"></i>Allow'
      })
  }

  blockFromPending() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const blockBtn = document.getElementById("confirmBlockBtn")
    blockBtn.disabled = true
    blockBtn.textContent = "Blocking..."

    fetch(`/social/block/${this.confirmUserId}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(response => response.json())
      .then(() => {
        this.confirmModalInstance.hide()
        this.removeFromPendingList(this.confirmUserId)
      })
      .finally(() => {
        blockBtn.disabled = false
        blockBtn.innerHTML = '<i class="bi bi-slash-circle me-1"></i>Block'
      })
  }

  removeFromPendingList(userId) {
    if (!this.hasPendingListTarget) return

    const item = this.pendingListTarget.querySelector(`[data-pending-id="${userId}"]`)
    if (item) item.remove()

    if (this.pendingListTarget.children.length === 0 && this.hasPendingSectionTarget) {
      this.pendingSectionTarget.remove()
    }
  }

  addToFollowersList(userId, username) {
    if (this.hasNoFollowersMessageTarget) {
      const cardBody = this.noFollowersMessageTarget.parentElement
      this.noFollowersMessageTarget.remove()
      const list = document.createElement("div")
      list.className = "list-group list-group-flush"
      list.dataset.followsTarget = "followersList"
      cardBody.appendChild(list)
    }

    if (!this.hasFollowersListTarget) return

    const list = this.followersListTarget
    if (list.querySelector(`[data-follower-id="${userId}"]`)) return

    const imageHtml = `<div class="flow-user-placeholder">${username.charAt(0).toUpperCase()}</div>`

    const item = document.createElement("div")
    item.className = "list-group-item d-flex align-items-center justify-content-between"
    item.dataset.followerId = userId
    item.innerHTML =
      `<div class="d-flex align-items-center gap-2">` +
        imageHtml +
        `<span>${username}</span>` +
      `</div>` +
      `<button type="button" class="btn btn-sm btn-outline-danger" ` +
        `data-action="click->follows#showBlockModal" ` +
        `data-user-id="${userId}" ` +
        `data-username="${username}"><i class="bi bi-slash-circle me-1"></i>Block</button>`

    list.insertBefore(item, list.firstChild)
  }

  clickOutside(event) {
    if (!this.queryTarget.contains(event.target) && !this.searchResultsTarget.contains(event.target)) {
      this.searchResultsTarget.classList.add("d-none")
    }
  }
}
